import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Pagination } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ScreeningResult, CommodityScreenRecord } from 'api/ExportHubAPI';
import EntityRecordDrawer, { ScreeningHit } from './EntityRecordDrawer';

interface Props {
  // The product's export_screening results, newest first is not assumed.
  screenings: ScreeningResult[];
  // True while an export_screening run is in flight.
  running: boolean;
  // Re-run the commodity screen (no AI).
  onRunScreen: () => void;
  // The screen searches the commodity index by HS code, so without one there
  // is nothing to search on — the UI says so instead of running an empty screen.
  htsCode: string | null;
}

const RECORDS_PER_PAGE = 10;

const RiskScreening: React.FC<Props> = ({
  screenings,
  running,
  onRunScreen,
  htsCode,
}) => {
  const hasHts = !!(htsCode || '').trim();
  const [displayedId, setDisplayedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const displayed =
    screenings.find((s) => s.id === displayedId) ||
    [...screenings].sort(
      (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
    )[0] ||
    null;

  const records: CommodityScreenRecord[] = displayed
    ? displayed.result_data?.records || []
    : [];

  const matched = useMemo(
    () =>
      records.filter(
        (r) =>
          !search ||
          `${r.hs_code} ${r.bite_lens} ${r.risk_country} ${r.list}`
            .toLowerCase()
            .includes(search.toLowerCase()),
      ),
    [records, search],
  );

  useEffect(() => {
    setPage(1);
    setSelectedIndex(-1);
  }, [search, displayedId]);

  const totalPages = Math.max(1, Math.ceil(matched.length / RECORDS_PER_PAGE));
  const curPage = Math.min(page, totalPages);
  const pageRecs = matched.slice(
    (curPage - 1) * RECORDS_PER_PAGE,
    curPage * RECORDS_PER_PAGE,
  );

  const hits: ScreeningHit[] = matched.map((r) => ({
    source:
      r.source && Object.keys(r.source).length > 0
        ? r.source
        : {
            'HS Code': r.hs_code,
            'BITE Lens': r.bite_lens,
            'BITE-Identified Risk Country': r.risk_country,
            'List Name': r.list,
            'Trade Type': r.trade_type,
          },
  }));

  // The screen is a commodity-index lookup by HS code. Without one there is
  // nothing to search, so say that rather than offering a run that finds nothing.
  if (!hasHts) {
    return (
      <div className='eh-panel eh-screen-empty'>
        <WarningAmberIcon style={{ fontSize: 28, color: 'var(--mid-color)' }} />
        <div className='export-hub-empty-title'>HTS code required</div>
        <div className='export-hub-empty-sub'>
          The risk screen looks this product up in the commodity lists by its
          HTS / Schedule B code. Add one in Product Details, then run the
          screen.
        </div>
      </div>
    );
  }

  // Empty state — no screen has run yet (e.g. a bulk-imported product).
  if (!displayed) {
    return (
      <div className='eh-panel eh-screen-empty'>
        <AutoAwesomeIcon
          style={{ fontSize: 28, color: 'var(--action-color)' }}
        />
        <div className='export-hub-empty-title'>No risk screening yet</div>
        <div className='export-hub-empty-sub'>
          Run a commodity screen and export threat evaluation for this product.
        </div>
        <button
          className='btn-primary'
          type='button'
          onClick={onRunScreen}
          disabled={running}
          style={{ marginTop: 12 }}
        >
          {running ? 'Running…' : 'Run Screen'}
        </button>
      </div>
    );
  }

  return (
    <div className='eh-panel eh-screen-panel'>
      <div className='eh-screen-head'>
        <div className='eh-panel-title' style={{ marginBottom: 0 }}>
          Export Risk Screening
        </div>
        <div className='eh-screen-head-right'>
          <span className='eh-detail-meta-text'>
            Screened {moment(displayed.version_date).format('MM/DD/YYYY')}
          </span>
        </div>
      </div>

      {/* Commodity screen hits */}
      <div className='eh-lens-toolbar'>
        <span className='eh-lens-count'>{matched.length} commodity hits</span>
        <div className='eh-facet-spacer' />
        <div className='data-search-wrapper'>
          <SearchIcon className='data-search-icon' style={{ fontSize: 18 }} />
          <input
            type='text'
            className='data-search-input'
            placeholder='Search hits'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <table className='runs-table eh-lens-table eh-lens-table-wide'>
        <thead>
          <tr>
            <th>HS Code</th>
            <th>BITE Lens</th>
            <th>Risk Country</th>
            <th>List</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {matched.length === 0 && (
            <tr>
              <td colSpan={5} className='eh-no-match'>
                No commodity-list hits for this screening.
              </td>
            </tr>
          )}
          {pageRecs.map((r, i) => (
            <tr
              key={i}
              className='hoverable-row'
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedIndex(matched.indexOf(r))}
            >
              <td>{r.hs_code}</td>
              <td>{r.bite_lens}</td>
              <td>{r.risk_country}</td>
              <td>{r.list}</td>
              <td style={{ textAlign: 'right' }}>
                <VisibilityOutlinedIcon
                  style={{ fontSize: 16, color: 'var(--text-tertiary)' }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {matched.length > RECORDS_PER_PAGE && (
        <div className='hb-pagination eh-records-pagination'>
          <span className='pagination-text'>
            Page {curPage} of {totalPages} · {matched.length} hits
          </span>
          <Pagination
            size='small'
            shape='rounded'
            variant='text'
            count={totalPages}
            page={curPage}
            onChange={(_e: any, p: any) => setPage(p)}
          />
        </div>
      )}

      {screenings.length > 1 && (
        <div className='eh-facet' style={{ marginTop: 10 }}>
          <span className='eh-ai-label'>
            <ExpandMoreIcon style={{ fontSize: 14, verticalAlign: 'middle' }} />{' '}
            Earlier screenings
          </span>
          <div className='eh-version-list'>
            {[...screenings]
              .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))
              .map((s) => (
                <button
                  key={s.id}
                  type='button'
                  className={`eh-version-chip${
                    s.id === displayed.id ? ' active' : ''
                  }`}
                  onClick={() => setDisplayedId(s.id)}
                >
                  {moment(s.created_at).format('MMM D, h:mm A')}
                </button>
              ))}
          </div>
        </div>
      )}

      <EntityRecordDrawer
        open={selectedIndex >= 0}
        hits={hits}
        index={selectedIndex}
        onNavigate={setSelectedIndex}
        onClose={() => setSelectedIndex(-1)}
      />
    </div>
  );
};

export default RiskScreening;
